/**
 * 地理信息工具类
 * 提供地理坐标转换、GeoJSON处理、距离计算等功能
 */

/**
 * 地球半径（千米）
 */
export const EARTH_RADIUS = 6371.009;
const iconv = require('iconv-lite');
/**
 * 经纬度坐标点
 * @typedef {Object} LatLng
 * @property {number} lat - 纬度
 * @property {number} lng - 经度
 */

/**
 * 度分秒转十进制度
 * @param {number} degrees - 度
 * @param {number} minutes - 分
 * @param {number} seconds - 秒
 * @param {string} direction - 方向（N/S/E/W）
 * @returns {number} 十进制度
 */
export function dmsToDecimal(degrees, minutes, seconds, direction) {
  let decimal = degrees + minutes / 60 + seconds / 3600;
  if (direction === 'S' || direction === 'W') {
    decimal = -decimal;
  }
  return decimal;
}

/**
 * 十进制度转度分秒
 * @param {number} decimal - 十进制度
 * @returns {Object} 度分秒对象
 */
export function decimalToDms(decimal) {
  const isNegative = decimal < 0;
  decimal = Math.abs(decimal);
  const degrees = Math.floor(decimal);
  const minutesDecimal = (decimal - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  const seconds = (minutesDecimal - minutes) * 60;
  
  return {
    degrees,
    minutes,
    seconds,
    direction: isNegative ? undefined : undefined
  };
}

/**
 * 计算两点间的直线距离（千米）
 * @param {LatLng} point1 - 第一个点
 * @param {LatLng} point2 - 第二个点
 * @returns {number} 距离（千米）
 */
export function calculateDistance(point1, point2) {
  const lat1 = toRadians(point1.lat);
  const lat2 = toRadians(point2.lat);
  const lon1 = toRadians(point1.lng);
  const lon2 = toRadians(point2.lng);
  
  const deltaLat = lat2 - lat1;
  const deltaLon = lon2 - lon1;
  
  const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) *
    Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  return EARTH_RADIUS * c;
}

/**
 * 角度转弧度
 * @param {number} degrees - 角度
 * @returns {number} 弧度
 */
export function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}

/**
 * 弧度转角度
 * @param {number} radians - 弧度
 * @returns {number} 角度
 */
export function toDegrees(radians) {
  return radians * (180 / Math.PI);
}

/**
 * 判断点是否在多边形内部
 * @param {LatLng} point - 要判断的点
 * @param {LatLng[]} polygon - 多边形顶点数组
 * @returns {boolean} 点是否在多边形内
 */
export function isPointInPolygon(point, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng, yi = polygon[i].lat;
    const xj = polygon[j].lng, yj = polygon[j].lat;
    
    const intersect = ((yi > point.lat) !== (yj > point.lat))
        && (point.lng < (xj - xi) * (point.lat - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * 将GeoJSON点转换为坐标对象
 * @param {Object} geoJsonPoint - GeoJSON点对象
 * @returns {LatLng} 坐标对象
 */
export function geoJsonPointToLatLng(geoJsonPoint) {
  if (!geoJsonPoint || geoJsonPoint.type !== 'Point' || !geoJsonPoint.coordinates) {
    throw new Error('无效的GeoJSON点对象');
  }
  return {
    lng: geoJsonPoint.coordinates[0],
    lat: geoJsonPoint.coordinates[1]
  };
}

/**
 * 将坐标对象转换为GeoJSON点
 * @param {LatLng} latLng - 坐标对象
 * @returns {Object} GeoJSON点对象
 */
export function latLngToGeoJsonPoint(latLng) {
  if (!latLng || typeof latLng.lat !== 'number' || typeof latLng.lng !== 'number') {
    throw new Error('无效的坐标对象');
  }
  return {
    type: 'Point',
    coordinates: [latLng.lng, latLng.lat]
  };
}

/**
 * 将GeoJSON多边形转换为坐标数组
 * @param {Object} geoJsonPolygon - GeoJSON多边形对象
 * @returns {LatLng[][]} 坐标数组
 */
export function geoJsonPolygonToLatLngs(geoJsonPolygon) {
  if (!geoJsonPolygon || geoJsonPolygon.type !== 'Polygon' || !geoJsonPolygon.coordinates) {
    throw new Error('无效的GeoJSON多边形对象');
  }
  return geoJsonPolygon.coordinates.map(ring => 
    ring.map(coords => ({ lng: coords[0], lat: coords[1] }))
  );
}

/**
 * 将坐标数组转换为GeoJSON多边形
 * @param {LatLng[][]} latLngs - 坐标数组
 * @returns {Object} GeoJSON多边形对象
 */
export function latLngsToGeoJsonPolygon(latLngs) {
  if (!Array.isArray(latLngs) || latLngs.length === 0) {
    throw new Error('无效的坐标数组');
  }
  return {
    type: 'Polygon',
    coordinates: latLngs.map(ring => 
      ring.map(point => [point.lng, point.lat])
    )
  };
}

/**
 * 获取多边形的中心点
 * @param {LatLng[]} polygon - 多边形顶点数组
 * @returns {LatLng} 中心点坐标
 */
export function getPolygonCenter(polygon) {
  let x = 0, y = 0;
  const n = polygon.length;
  
  for (let i = 0; i < n; i++) {
    x += polygon[i].lng;
    y += polygon[i].lat;
  }
  
  return { lng: x / n, lat: y / n };
}

/**
 * 简化GeoJSON数据
 * @param {Object} geoJson - GeoJSON对象
 * @param {number} tolerance - 容差（越小保留细节越多）
 * @returns {Object} 简化后的GeoJSON对象
 */
export function simplifyGeoJson(geoJson, tolerance = 0.001) {
  if (!geoJson || !geoJson.type) {
    return geoJson;
  }
  
  // 简单实现，实际项目中可引入更复杂的简化算法
  switch (geoJson.type) {
    case 'FeatureCollection':
      return {
        ...geoJson,
        features: geoJson.features.map(feature => simplifyGeoJson(feature, tolerance))
      };
    case 'Feature':
      return {
        ...geoJson,
        geometry: simplifyGeoJson(geoJson.geometry, tolerance)
      };
    case 'Polygon':
      return {
        ...geoJson,
        coordinates: geoJson.coordinates.map(ring => 
          simplifyPolyline(ring, tolerance)
        )
      };
    case 'LineString':
      return {
        ...geoJson,
        coordinates: simplifyPolyline(geoJson.coordinates, tolerance)
      };
    default:
      return geoJson;
  }
}

/**
 * 简化折线
 * @param {Array} points - 点数组
 * @param {number} tolerance - 容差
 * @returns {Array} 简化后的点数组
 */
function simplifyPolyline(points, tolerance) {
  // 简化的Douglas-Peucker算法实现
  if (points.length <= 2) return points;
  
  // 找到距离最大的点
  let maxDist = 0;
  let maxIndex = 0;
  
  for (let i = 1; i < points.length - 1; i++) {
    const dist = perpendicularDistance(points[i], points[0], points[points.length - 1]);
    if (dist > maxDist) {
      maxDist = dist;
      maxIndex = i;
    }
  }
  
  // 如果最大距离大于容差，递归简化
  if (maxDist > tolerance) {
    const left = simplifyPolyline(points.slice(0, maxIndex + 1), tolerance);
    const right = simplifyPolyline(points.slice(maxIndex), tolerance);
    return [...left.slice(0, -1), ...right];
  } else {
    // 否则，保留起点和终点
    return [points[0], points[points.length - 1]];
  }
}

/**
 * 计算点到线段的垂直距离
 * @param {Array} point - 点坐标
 * @param {Array} lineStart - 线段起点
 * @param {Array} lineEnd - 线段终点
 * @returns {number} 垂直距离
 */
function perpendicularDistance(point, lineStart, lineEnd) {
  const A = point[0] - lineStart[0];
  const B = point[1] - lineStart[1];
  const C = lineEnd[0] - lineStart[0];
  const D = lineEnd[1] - lineStart[1];
  
  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;
  
  if (lenSq !== 0) {
    param = dot / lenSq;
  }
  
  let xx, yy;
  
  if (param < 0) {
    xx = lineStart[0];
    yy = lineStart[1];
  } else if (param > 1) {
    xx = lineEnd[0];
    yy = lineEnd[1];
  } else {
    xx = lineStart[0] + param * C;
    yy = lineStart[1] + param * D;
  }
  
  const dx = point[0] - xx;
  const dy = point[1] - yy;
  
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * 坐标系转换工具
 */
export const coordinateTransform = {
  /**
   * WGS84转GCJ02（火星坐标系）
   * @param {number} lat - WGS84纬度
   * @param {number} lng - WGS84经度
   * @returns {LatLng} GCJ02坐标
   */
  wgs84ToGcj02: function(lat, lng) {
    // 简化实现，实际项目中应使用更精确的转换算法
    if (this.outOfChina(lat, lng)) {
      return { lat, lng };
    }
    
    let dLat = this.transformLat(lng - 105.0, lat - 35.0);
    let dLng = this.transformLng(lng - 105.0, lat - 35.0);
    const radLat = lat / 180.0 * Math.PI;
    let magic = Math.sin(radLat);
    magic = 1 - this.ee * magic * magic;
    const sqrtMagic = Math.sqrt(magic);
    dLat = (dLat * 180.0) / ((this.a * (1 - this.ee)) / (magic * sqrtMagic) * Math.PI);
    dLng = (dLng * 180.0) / (this.a / sqrtMagic * Math.cos(radLat) * Math.PI);
    
    return { 
      lat: lat + dLat, 
      lng: lng + dLng 
    };
  },
  
  /**
   * GCJ02转WGS84
   * @param {number} lat - GCJ02纬度
   * @param {number} lng - GCJ02经度
   * @returns {LatLng} WGS84坐标
   */
  gcj02ToWgs84: function(lat, lng) {
    // 简化实现，实际项目中应使用更精确的转换算法
    if (this.outOfChina(lat, lng)) {
      return { lat, lng };
    }
    
    let dLat = this.transformLat(lng - 105.0, lat - 35.0);
    let dLng = this.transformLng(lng - 105.0, lat - 35.0);
    const radLat = lat / 180.0 * Math.PI;
    let magic = Math.sin(radLat);
    magic = 1 - this.ee * magic * magic;
    const sqrtMagic = Math.sqrt(magic);
    dLat = (dLat * 180.0) / ((this.a * (1 - this.ee)) / (magic * sqrtMagic) * Math.PI);
    dLng = (dLng * 180.0) / (this.a / sqrtMagic * Math.cos(radLat) * Math.PI);
    
    return { 
      lat: lat - dLat, 
      lng: lng - dLng 
    };
  },
  
  // 辅助常量和方法
  a: 6378245.0, // 长半轴
  ee: 0.00669342162296594323, // 扁率
  
  transformLat: function(x, y) {
    let ret = -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x));
    ret += (20.0 * Math.sin(6.0 * x * Math.PI) + 20.0 * Math.sin(2.0 * x * Math.PI)) * 2.0 / 3.0;
    ret += (20.0 * Math.sin(y * Math.PI) + 40.0 * Math.sin(y / 3.0 * Math.PI)) * 2.0 / 3.0;
    ret += (160.0 * Math.sin(y / 12.0 * Math.PI) + 320 * Math.sin(y * Math.PI / 30.0)) * 2.0 / 3.0;
    return ret;
  },
  
  transformLng: function(x, y) {
    let ret = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x));
    ret += (20.0 * Math.sin(6.0 * x * Math.PI) + 20.0 * Math.sin(2.0 * x * Math.PI)) * 2.0 / 3.0;
    ret += (20.0 * Math.sin(x * Math.PI) + 40.0 * Math.sin(x / 3.0 * Math.PI)) * 2.0 / 3.0;
    ret += (150.0 * Math.sin(x / 12.0 * Math.PI) + 300.0 * Math.sin(x / 30.0 * Math.PI)) * 2.0 / 3.0;
    return ret;
  },
  
  outOfChina: function(lat, lng) {
    if (lng < 72.004 || lng > 137.8347) return true;
    if (lat < 0.8293 || lat > 55.8271) return true;
    return false;
  }
}

export const geojsonToShp = {
    encodePolygon: function(geojson,bbox) {
      // --------------------------
      // 步骤1：计算几何数据总字节数（用于动态分配缓冲区）
      // --------------------------
      let allRings = [];
      geojson.features.forEach(feature => {
        const geom = feature.geometry;
        geom.coordinates.forEach(polygon => {
            allRings.push(...polygon); // MultiPolygon：[[[[x,y],...], ...], ...]
          });
      });

      // --------------------------
      // 步骤2：计算总字节数和边界框
      // --------------------------
      // 计算总点数
      let totalPoints = 0;
      allRings.forEach(ring => { totalPoints += ring.length; });

      // 计算边界框（minX, minY, maxX, maxY）
      // let bbox = {
      //   minX: Infinity,
      //   minY: Infinity,
      //   maxX: -Infinity,
      //   maxY: -Infinity
      // };
      // allRings.forEach(ring => {
      //   ring.forEach(([x, y]) => {
      //     if (x < bbox.minX) bbox.minX = x;
      //     if (y < bbox.minY) bbox.minY = y;
      //     if (x > bbox.maxX) bbox.maxX = x;
      //     if (y > bbox.maxY) bbox.maxY = y;
      //   });
      // });
      // // 处理空几何的极端情况
      // if (totalPoints === 0) {
      //   bbox = { minX: 0, minY: 0, maxX: 0, maxY: 0 };
      // }

      // 计算各部分字节数
      const recordHeaderBytes = 8; // 记录头（4字节记录号 + 4字节长度）
      const shapeTypeBytes = 4;    // 形状类型（Polygon=5）
      const ringCountBytes = 4;    // 环数量
      const totalPointsCountBytes = 4; // 总点数（4字节）
      const bboxBytes = 32; // 边界框（4*8字节）
      const pointsCountBytes = allRings.length * 4; // 每个环的点数量（4字节/个）
      const coordinatesBytes = totalPoints * 16;    // 坐标（每个点x+y共16字节）
      const totalBytes = 100 + (recordHeaderBytes + shapeTypeBytes + bboxBytes + ringCountBytes + totalPointsCountBytes) * geojson.features.length + pointsCountBytes + coordinatesBytes; // 总字节数（头100 + 记录）
      const shxTotalBytes = 100 + geojson.features.length * 8; // 100字节头 + 每个记录8字节

      //console.log(totalBytes);
      // --------------------------
      // 步骤3：创建缓冲区并写入数据
      // --------------------------
      const shpBuffer = new ArrayBuffer(totalBytes);
      const shpView = new DataView(shpBuffer);
      let shpOffset = 0;
      
      const shxBuffer = new ArrayBuffer(shxTotalBytes);
      const shxView = new DataView(shxBuffer);
      let shxOffset = 0;

      //console.log("SHP总字节数",totalBytes,"SHX总字节数",shxTotalBytes);
      

      // --------------------------
      // 步骤4：写入100字节文件头（严格遵循Shapefile规范字节序）
      // --------------------------
      // 0-3：文件代码（固定9994，大端序）
      shpView.setInt32(shpOffset, 9994, false);      
      shpOffset += 4;

      shxView.setInt32(shxOffset, 9994, false);      
      shxOffset += 4;

      // 4-23：预留20字节（填0）
      for (let i = 0; i < 20; i++) {
        shpView.setUint8(shpOffset++, 0);  
        shxView.setUint8(shxOffset++, 0);      
      }

      // 24-27：文件长度（总字节数/2，大端序）
      shpView.setInt32(shpOffset, totalBytes / 2, false);      
      shpOffset += 4;
      shxView.setInt32(shxOffset, shxTotalBytes / 2, false);      
      shxOffset += 4;

      // 28-31：版本（固定1000，小端序）
      shpView.setInt32(shpOffset, 1000, true);    
      shpOffset += 4;
      shxView.setInt32(shxOffset, 1000, true);    
      shxOffset += 4;

      // 32-35：形状类型（5=Polygon，小端序）
      shpView.setInt32(shpOffset, 5, true);      
      shpOffset += 4;
      shxView.setInt32(shxOffset, 5, true);      
      shxOffset += 4;

      // 36-67：边界框（minX, minY, maxX, maxY，小端序）
      shpView.setFloat64(shpOffset, bbox[0], true);      
      shpOffset += 8;
      shpView.setFloat64(shpOffset, bbox[1], true);      
      shpOffset += 8;
      shpView.setFloat64(shpOffset, bbox[2], true);    
      shpOffset += 8;
      shpView.setFloat64(shpOffset, bbox[3], true);     
      shpOffset += 8;

      shxView.setFloat64(shxOffset, bbox[0], true);      
      shxOffset += 8;
      shxView.setFloat64(shxOffset, bbox[1], true);      
      shxOffset += 8;
      shxView.setFloat64(shxOffset, bbox[2], true);    
      shxOffset += 8;
      shxView.setFloat64(shxOffset, bbox[3], true);     
      shxOffset += 8;

      // 68-99：Z坐标和M坐标范围（未使用，填0）
      for (let i = 0; i < 32; i++) { // 32字节（4个double，每个8字节）
        shpView.setUint8(shpOffset++, 0);
        shxView.setUint8(shxOffset++, 0);      
      }

      // --------------------------
      // 步骤5：写入几何记录（从100字节开始）
      // --------------------------
      // 记录头（8字节）
      for(let i = 0; i < geojson.features.length; i++){
        let feature = geojson.features[i];
        let featureInfo = this.calculateFeatrueInfo(feature);  
        //console.log(featureInfo);   
        shpView.setInt32(shpOffset, i+1, false); // 记录号（从1开始，大端序）
          //console.log(shpOffset,i+1); 
          shxView.setInt32(shxOffset, shpOffset / 2, false); // 记录偏移（字节数/2，大端序）  
          //console.log("SHX偏移(shp文件中偏移量)",shpOffset / 2);      
          shxOffset += 4;    
          shxView.setInt32(shxOffset, (featureInfo.totalBytes - 8) / 2, false); // 记录长度（字节数/2，大端序）   
          //console.log("SHX记录长度",(featureInfo.totalBytes - 8) / 2);          
          shxOffset += 4;   
        shpOffset += 4;
        shpView.setInt32(shpOffset, (featureInfo.totalBytes - 8) / 2, false); // 记录长度（字节数/2，大端序）  
        //console.log(shpOffset,(featureInfo.totalBytes - 8) / 2);     
        shpOffset += 4;
        // 几何内容
        shpView.setInt32(shpOffset, 5, true); // 形状类型（5=Polygon，小端序） 
        //console.log(shpOffset,5);      
        shpOffset += 4;
        //feature的bbox 坐标范围
        shpView.setFloat64(shpOffset, feature.geometry.bbox[0], true);      
        shpOffset += 8;
        shpView.setFloat64(shpOffset, feature.geometry.bbox[1], true);     
        shpOffset += 8;
        shpView.setFloat64(shpOffset, feature.geometry.bbox[2], true);       
        shpOffset += 8;
        shpView.setFloat64(shpOffset, feature.geometry.bbox[3], true);
        shpOffset += 8;
        //子环数量
        shpView.setInt32(shpOffset, featureInfo.ringCount, true); 
        //console.log(shpOffset,featureInfo.ringCount);     
        shpOffset += 4;
        //坐标点数
        shpView.setInt32(shpOffset, featureInfo.pointsCount, true);   
        //console.log(shpOffset,featureInfo.pointsCount);     
        shpOffset += 4;
        //Parts数组
        for(let j = 0; j < featureInfo.ringCount; j++){
          shpView.setInt32(shpOffset, featureInfo.pointsIndex[j], true); // 子环索引（小端序）   
          //console.log(shpOffset,featureInfo.pointsIndex[j]);     
          shpOffset += 4;
        }
        feature.geometry.coordinates.forEach(polygon=>{
          polygon.forEach(ring=>{
            ring.forEach(([x, y]) => {
              shpView.setFloat64(shpOffset, x, true); // X坐标   
              //console.log(shpOffset,x);     
              shpOffset += 8;
              shpView.setFloat64(shpOffset, y, true); // Y坐标
              //console.log(shpOffset,y);     
              shpOffset += 8;
            });
          })
        })
      }
     //console.log("shp内容",new Uint8Array(shpBuffer),"SHX内容",new Uint8Array(shxBuffer));
      const shpBlob = new Blob([new Uint8Array(shpBuffer)]);
      const shxBlob = new Blob([new Uint8Array(shxBuffer)]);
      return {shpBlob,shxBlob};
    },

    // 辅助方法：计算多边形的边界框（minX, minY, maxX, maxY）
    calculateBBox: function(geojson) {
      let minX = Infinity, minY = Infinity;
      let maxX = -Infinity, maxY = -Infinity;

      geojson.features.forEach(feature=>{
        let bbox = feature.geometry.bbox;
        minX = Math.min(minX, bbox[0]);
        minY = Math.min(minY, bbox[1]);
        maxX = Math.max(maxX, bbox[2]);
        maxY = Math.max(maxY, bbox[3]);
      })
      return [minX, minY, maxX, maxY];
    },

    //计算单个feature的信息
    calculateFeatrueInfo: function(feature) {     
      let geometry = feature.geometry;
      let headBytes = 8;
      let bboxBytes = 32;
      let shapeTypeBytes = 4;    // 形状类型（Polygon=5）
      let ringCountBytes = 4;    // 环数量      
      let ringCount = 0;
      let pointsCount = 0;
      let pointsIndex = [];
      let preIndex = 0;    
      geometry.coordinates.forEach(polygon => {
        ringCount += polygon.length;
        polygon.forEach(ring => {
          pointsIndex.push(preIndex);
          preIndex += ring.length;
          pointsCount += ring.length;
        });
      });
      let pointsCountBytes = ringCount * 4; // 每个环的点数量（4字节/个）
      let coordinatesBytes = pointsCount * 16;    // 坐标（每个点x+y共16字节）
      let totalBytes = headBytes + shapeTypeBytes + bboxBytes + ringCountBytes + pointsCountBytes + coordinatesBytes + pointsIndex.length * 4;
      return {totalBytes,ringCount,pointsCount,pointsIndex}
    },

    encodeDBF: function(features,columns) {
      // --------------------------
      // 步骤1：定义字段（示例）
      // --------------------------
      const fields = [];

      columns.forEach(column=>{
        if(column.isNum){
          fields.push({ name: column.name, type: 'N', length: 10, decimal: 3 });
        }else{
          fields.push({ name: column.name, type: 'C', length: 50 });
        }
      })

      // 校验字段合法性（DBF 规范限制）
      fields.forEach(field => {
        if (field.name.length > 10) throw new Error(`字段名 ${field.name} 过长（最多10字符）`);
        if (!['C', 'N', 'D', 'L'].includes(field.type)) throw new Error(`不支持的字段类型 ${field.type}`);
      });


      // --------------------------
      // 步骤2：计算缓冲区大小
      // --------------------------
      const recordCount = features.length;                // 记录数
      const fieldCount = fields.length;                    // 字段数
      const headerLength = 32 + (fieldCount * 32) + 1;    // 头长度：32字节头 + 32字节/字段 + 1字节终止符
      const recordLength = fields.reduce((sum, f) => sum + f.length, 0) + 1; // 每条记录长度：字段总长度 + 1字节删除标记
      const totalBytes = headerLength + (recordCount * recordLength); // 总大小


      // --------------------------
      // 步骤3：初始化缓冲区
      // --------------------------
      const buffer = new ArrayBuffer(totalBytes);
      const view = new DataView(buffer);
      let offset = 0;


      // --------------------------
      // 步骤4：写入DBF头信息（32字节）
      // --------------------------
      // 0: 版本号（0x03 = dBase III）
      view.setUint8(offset++, 0x03);

      // 1-3: 最后修改日期（年+月+日，相对于1900年）
      const now = new Date();
      view.setUint8(offset++, now.getFullYear() - 1900); // 年
      view.setUint8(offset++, now.getMonth() + 1);       // 月（1-12）
      view.setUint8(offset++, now.getDate());            // 日

      // 4-7: 记录总数（小端序32位整数）
      view.setInt32(offset, recordCount, true);
      offset += 4;

      // 8-9: 头长度（小端序16位整数）
      view.setInt16(offset, headerLength, true);
      offset += 2;

      // 10-11: 每条记录长度（小端序16位整数）
      view.setInt16(offset, recordLength, true);
      offset += 2;

      // 12-31: 预留字段（填0）
      for (let i = 0; i < 20; i++) view.setUint8(offset++, 0);


      // --------------------------
      // 步骤5：写入字段定义（每个字段32字节）
      // --------------------------
      fields.forEach(field => {
        // 0-10: 字段名（11字节，不足补空格）
        //const nameBytes = new TextEncoder().encode(field.name.padEnd(11, ' ').slice(0, 11));
        const nameBytes = iconv.encode(field.name.padEnd(11, ' ').slice(0, 11), 'gbk');
        for (let i = 0; i < 11; i++) view.setUint8(offset + i, nameBytes[i] || 0x20);
        offset += 11;

        // 11: 字段类型（C/N/D/L）
        view.setUint8(offset++, field.type.charCodeAt(0));

        // 12-15: 字段偏移量（预留，填0）
        for (let i = 0; i < 4; i++) view.setUint8(offset++, 0);

        // 16: 字段长度（字符串：最大254；数值：根据精度）
        view.setUint8(offset++, field.length);

        // 17: 小数位数（仅数值型有效）
        view.setUint8(offset++, field.decimal || 0);

        // 18-31: 预留字段（填0）
        for (let i = 0; i < 14; i++) view.setUint8(offset++, 0);
      });


      // --------------------------
      // 步骤6：字段定义终止符（1字节）
      // --------------------------
      view.setUint8(offset++, 0x0D); // 0x0D = 回车符，标识字段定义结束


      // --------------------------
      // 步骤7：写入记录数据
      // --------------------------
      features.forEach(feature => {
        // 每条记录开头：删除标记（1字节，0x20=未删除，0x2A=已删除）
        view.setUint8(offset++, 0x20);

        // 按字段写入属性值
        fields.forEach(field => {
          const value = feature.properties[field.name] ?? ''; // 处理undefined
          let valueStr = '';
          let num = 0;

          // 根据字段类型格式化
          switch (field.type) {
            case 'C': // 字符串：截断或补空格至指定长度
              valueStr = value.toString().padEnd(field.length, ' ').slice(0, field.length);
              break;
            case 'N': // 数值：格式化小数位，右对齐补空格
              num = Number(value) || 0;
              valueStr = num.toFixed(field.decimal).padStart(field.length, ' ').slice(-field.length);
              break;
            // 可扩展支持日期（D）、逻辑（L）等类型
          }

          // 写入字节（使用系统默认编码，DBF通常为ASCII或Windows-1252）
          //const valueBytes = new TextEncoder().encode(valueStr);
          const valueBytes = iconv.encode(valueStr, 'gbk');
          for (let i = 0; i < field.length; i++) {
            view.setUint8(offset + i, valueBytes[i] || 0x20); // 不足补空格
          }
          offset += field.length;
        });
      });


      return new Uint8Array(buffer);
    },

    encodeSHX:function(featureCount,bbox){
        const buffer = new ArrayBuffer(100 + featureCount * 8);
        const view = new DataView(buffer);

        // 头信息（与 .shp 头信息一致）
        view.setInt32(0, 9994, false); // 大端序（Shapefile头多数字段用大端序）
        // 4-23：未使用（预留20字节，填0）
        for (let i = 4; i < 24; i++) view.setUint8(i, 0);
        // 24-27：文件长度（总字节数 / 2，大端序）
        view.setInt32(24, buffer.length / 2, false);
        // 28-31：版本（固定为1000，小端序）
        view.setInt32(28, 1000, true);
        // 32-35：形状类型（5=Polygon，小端序）
        view.setInt32(32, 5, true);
        // 36-67：边界框（minX, minY, maxX, maxY，小端序，需计算）        
        view.setFloat64(36, bbox.minX, true);  // minX
        view.setFloat64(44, bbox.minY, true);  // minY
        view.setFloat64(52, bbox.maxX, true);  // maxX
        view.setFloat64(60, bbox.maxY, true);  // maxY
        // 68-99：Z坐标范围和M值范围（未使用，填0）
        for (let i = 68; i < 100; i++) view.setUint8(i, 0);        

        // 索引条目：每个条目占 8 字节（偏移量 + 长度）
        let offset = 100;
        let currentOffset = 100; // .shp 文件头之后的起始偏移
        for(let i = 0;i<featureCount;i++){
            view.setInt32(offset, currentOffset / 2, true); // 偏移量（以 2 字节为单位）
            view.setInt32(offset + 4, 0, true); // 长度（以 2 字节为单位）
            offset += 8;
            currentOffset += 0;
        }
        // featureCount.forEach(shp => {
        //     view.setInt32(offset, currentOffset / 2, true); // 偏移量（以 2 字节为单位）
        //     view.setInt32(offset + 4, shp.length / 2, true); // 长度（以 2 字节为单位）
        //     offset += 8;
        //     currentOffset += shp.length;
        // });

        return new Uint8Array(buffer);
    },

     generateShapefile:function(geoJSON,columns) {
        const { features } = geoJSON;
        //const shpBuffers = [];
        const dbfBuffer = this.encodeDBF(features,columns);
        const bbox = this.calculateBBox(geoJSON); // 计算边界框的方法 

        // // 逐个编码几何
        // features.forEach(feature => {
        //     if (feature.geometry.type === 'Polygon') {
        //     shpBuffers.push(this.encodePolygon(feature.geometry));
        //     } else if (feature.geometry.type === 'MultiPolygon') {
        //     // 多多边形需特殊处理（拆分或合并环）
        //     feature.geometry.coordinates.forEach(polygon => {
        //         shpBuffers.push(this.encodePolygon({ coordinates: polygon }));
        //     });
        //     }
        //     // ... 处理其他几何类型 ...
        // });

        const {shpBlob,shxBlob} = this.encodePolygon(geoJSON,bbox);       
        
        const prjBuffer = iconv.encode(
            'GEOGCS["China Geodetic Coordinate System 2000",DATUM["China_2000",SPHEROID["CGCS2000",6378137.0,298.257222101]],PRIMEM["Greenwich",0.0],UNIT["degree",0.017453292519943295],AXIS["Longitude",EAST],AXIS["Latitude",NORTH]]'
        ,'gbk');
        // 返回所有文件的二进制数据
        return {
            'output.shp': shpBlob,
            'output.shx': shxBlob,
            'output.dbf': new Blob([dbfBuffer]),
            'output.prj': new Blob([prjBuffer])
        };
    }
};

/**
 * 导出默认对象，方便整体导入
 */
export default {
  EARTH_RADIUS,
  dmsToDecimal,
  decimalToDms,
  calculateDistance,
  toRadians,
  toDegrees,
  isPointInPolygon,
  geoJsonPointToLatLng,
  latLngToGeoJsonPoint,
  geoJsonPolygonToLatLngs,
  latLngsToGeoJsonPolygon,
  getPolygonCenter,
  simplifyGeoJson,
  coordinateTransform,
  geojsonToShp
};
